/**
 * App.js — Root application component for the Leo Cussen store.
 *
 * Responsibilities:
 *  - Defines global environment config (staging vs live URLs).
 *  - Manages all shared state: cart, auth, popups, wishlist, and page data.
 *  - Wires up Shopify Storefront GraphQL mutations for cart operations.
 *  - Renders the top-level Router with all application routes.
 *  - Renders global overlay components (Header, Footer, modals, cookie banner).
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { useMutation } from "@apollo/react-hooks";
import axios from "axios";
import CookieConsent from "react-cookie-consent";

// ─── Global styles ────────────────────────────────────────────────────────────
import "bootstrap/dist/css/bootstrap.min.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./App.css";

// ─── Page / route components ──────────────────────────────────────────────────
import { Header } from "./components/Header/Header";
import { Footer } from "./components/Footer/Footer";
import { ProductPage } from "./components/ProductListing/ProductPage";
import { HomePage } from "./components/DefaultPage/HomePage";
import { Attendees } from "./components/CheckoutProcess/Attendees";
import { Thankyou } from "./components/Thank-you/Thankyou";
import { SIngleProduct } from "./components/SingleProduct/SIngleProduct";
import { Search } from "./components/Search/Search";
import { ResetPassword } from "./components/resetPassword/ResetPassword";
import { ActivateAccountGuest } from "./components/activateAccount/ActivateAccountGuest";
import { NotFound } from "./components/DefaultPage/notFound";
import ScrollToTop from "./components/ScrollToTop";

// ─── Admin / account pages ────────────────────────────────────────────────────
import { AdminMyEvents } from "./components/Admin/AdminMyEvents";
import { AdminOrders } from "./components/Admin/AdminOrders";
import { AdminCPDPoints } from "./components/Admin/AdminCPDPoints";
import { AdminMyLibrary } from "./components/Admin/AdminMyLibrary";
import { AdminProfile } from "./components/Admin/AdminProfile";
import { AdminWishlist } from "./components/Admin/AdminWishlist";

// ─── Modal / overlay components ───────────────────────────────────────────────
import { Login } from "./components/Modals/Login";
import { CreateAccount } from "./components/Modals/CreateAccount";
import { CartSidebar } from "./components/Modals/CartSidebar";
import { AboutPage } from "./components/Modals/AboutUs";
import { SignUp } from "./components/Modals/SignUp";
import { CanWeHelp } from "./components/Modals/CanWeHelp";
import { CookiePref } from "./components/Modals/CookiePref";

// ─── GraphQL mutations ────────────────────────────────────────────────────────
import { createCheckout } from "./components/grappql/Checkout/mutation/createCheckout";
import { checkoutLineItemsAdd } from "./components/grappql/Checkout/mutation/checkoutLineItemsAdd";
import { checkoutLineItemsUpdate } from "./components/grappql/Checkout/mutation/checkoutLineItemsUpdate";
import { checkoutLineItemsRemove } from "./components/grappql/Checkout/mutation/checkoutLineItemsRemove";



// ─── Constants / config ───────────────────────────────────────────────────────

/** Toggle between "staging" and "live" to switch API targets. */
const STORE_ENV = "live";

const config = {
  staging: {
    EngineURL: "https://leoenginestg.sfstaging.com.au/",
    AdminPanelURL: "https://phplaravel-1064140-3725363.cloudwaysapps.com/",
    storeURL: "https://leostorestg.sfstaging.com.au/",
    API_KEY: "z9P6qIDImtTluKD5UMjtcQoTvt6bnVRG"
  },
  live: {
    EngineURL: "https://engine.leocussen.edu.au/",
    AdminPanelURL: "https://storeadmin.leocussen.edu.au/",
    storeURL: "https://store.leocussen.edu.au/",
    API_KEY: "z9P6qIDImtTluKD5UMjtcQoTvt6bnVRG"

  },
};

const { EngineURL, AdminPanelURL, storeURL, API_KEY } = config[STORE_ENV];

/**
 * Maps each program-structure slug to its card thumbnail image URL.
 * Used to display the correct image on product cards throughout the store.
 */
const Program_structure_images = [
  {
    "cpd-2-day-workshop": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/LeoCussen_Brand-mark_RGB_Colour.png",
  },
  {
    "cpd-seminar": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Seminar.jpg",
  },
  {
    "cpd-seminar-(1-hour)": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Seminar.jpg",
  },
  {
    "cpd-seminar-(2-hour)": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Seminar.jpg",
  },
  {
    "cpd-online-module": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Online%20Module.jpg",
  },
  {
    "cpd-half-day-conference": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Conference.jpg",
  },
  {
    "online-intensive-(to-download)": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/Webcast%20Package.jpg",
  },
  {
    "cpd-workshop---essential": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Workshop.jpg",
  },
  {
    "cpd-intensive": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Intensive.jpg",
  },
  {
    "cpd-intensive-(3-point)": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Intensive.jpg",
  },
  {
    "cpd-intensive-(4-point)": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Intensive.jpg",
  },
  {
    "mental-health-first-aid-course":
      "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/Mental%20Health%20First%20Aid.jpg",
  },
  {
    "online-intensive-(to-download)": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/Webcast%20Package.jpg",
  },
  {
    "book": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/Book.jpg",
  },
  {
    "corporate-division": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/Corporate%20Training%20Division.jpg",
  },
  {
    "practice-management-course":
      "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/Practice%20Management%20Course.jpg",
  },
  {
    "traineeship-(swt)-workshop":
      "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/SLT%20Supervised%20Legal%20Training.jpg",
  },
  {
    "traineeship-(slt)-1-day-workshop":
      "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/SLT%20Supervised%20Legal%20Training.jpg",
  },
  {
    "traineeship-(slt)-1.5-day-workshop":
      "  https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/SLT%20Supervised%20Legal%20Training.jpg",
  },
  {
    "traineeship-(slt)-2-day-workshop":
      "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/SLT%20Supervised%20Legal%20Training.jpg",
  },
  {
    "traineeship-(slt)-3-day-workshop":
      "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/SLT%20Supervised%20Legal%20Training.jpg",
  },
  {
    "traineeship-(slt)-5-day-workshop":
      "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/SLT%20Supervised%20Legal%20Training.jpg",
  },
  {
    "traineeship-(slt)-half-day-workshop":
      "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/SLT%20Supervised%20Legal%20Training.jpg",
  },
  {
    "cpd-conference": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Conference.jpg",
  },
  {
    "cpd-full-day-conference": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Conference.jpg",
  },
  {
    "cpd-mini-conference": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Conference.jpg",
  },
  {
    "cpd-workshop---advocacy": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Workshop.jpg",
  },
  {
    "cpd-workshop---platinum": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Workshop.jpg",
  },
  {
    "legal-support-staff": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/Legal%20Support%20Staff.jpg",
  },
  {
    "medicare-billing-course": "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/Medicare%20Billing%20Course.jpg",
  },
];

/**
 * Maps each program-structure slug to its brand accent colour.
 * Used to tint product card headers / labels.
 */
const Program_structure_color = [
  { "cpd-seminar": "#70c0c7" },
  { "cpd-seminar-(1-hour)": "#70c0c7" },
  { "cpd-seminar-(2-hour)": "#70c0c7" },
  { "cpd-online-module": "#84be9c" },
  { "cpd-half-day-conference": "#987C88" },
  { "online-intensive-(to-download)": "#8cafbd" },
  { "cpd-workshop---essential": "#fec010" },
  { "cpd-intensive": "#bc318c" },
  { "cpd-intensive-(3-point)": "#bc318c" },
  { "cpd-intensive-(4-point)": "#bc318c" },
  { "mental-health-first-aid-course": "#bc318c" },
  { book: "#bc318c" },
  { "corporate-division": "#84be9c" },
  { "practice-management-course": "#987C88" },
  { "traineeship-(swt)-workshop": "#01293a" },
  { "traineeship-(slt)-1-day-workshop": "#01293a" },
  { "traineeship-(slt)-1.5-day-workshop": "#01293a" },
  { "traineeship-(slt)-2-day-workshop": "#01293a" },
  { "traineeship-(slt)-3-day-workshop": "#01293a" },
  { "traineeship-(slt)-5-day-workshop": "#01293a" },
  { "traineeship-(slt)-half-day-workshop": "#01293a" },
  { "cpd-conference": "#01293a" },
  { "cpd-full-day-conference": "#01293a" },
  { "cpd-mini-conference": "#01293a" },
  { "cpd-workshop---advocacy": "#fec010" },
  { "cpd-workshop---platinum": "#fec010" },
  { "legal-support-staff": "#bc318c" },
];

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * App — root component.
 *
 * Owns all global state and passes handlers down to child routes/modals.
 * No props accepted (rendered directly by index.js inside ApolloProvider).
 */
function App() {

  // ─── Tracking param capture (session-only, leo_ prefix) ──────────────────────
  useEffect(() => {
    var paramsToTrack = [
      'tracking_link',
      'utm_campaign',
      'utm_content',
      'utm_medium',
      'utm_source',
      'utm_term',
      'gclid',
      'fbclid',
      'msclkid'
    ];

    var urlParams = new URLSearchParams(window.location.search);

    paramsToTrack.forEach(function (param) {
      var value = urlParams.get(param);
      if (value) {
        document.cookie =
          'leo_' + param + '=' + encodeURIComponent(value) +
          '; path=/; SameSite=Lax; Secure';
      }
    });
  }, []);

  // Clear checkout data when landing on the thank-you page so the cart resets.
  if (window.location.pathname === "/thank-you") {
    localStorage.removeItem("checkout_id");
    localStorage.removeItem("cart_session");
    localStorage.removeItem("cart_session_sku");
  }

  // ── Cart quantity / amount display state ─────────────────────────────────
  const [Cartqty, setCartqty] = useState(0);
  const [CartAmount, setCartAmount] = useState(0.0);

  /** Updates the cart item count shown in the header badge. */
  const UpdateCartQty = (val) => {
    setCartqty(val);
  };

  /** Updates the cart total amount shown in the header. */
  const UpdateCartAmount = (val) => {
    setCartAmount(val);
  };

  // ── Auth state ───────────────────────────────────────────────────────────

  /** Reads the persisted login flag from localStorage on first render. */
  const [useLoggedIn, setUserLoggedIn] = useState(() => {
    const cart_item = localStorage.getItem("isLoggedin");
    return cart_item !== null && (cart_item === true || cart_item === "true") ? true : false;
  });

  // ── Offer-bar visibility ─────────────────────────────────────────────────

  const [offerBar, setofferBar] = useState(() => {
    const cart_item = localStorage.getItem("offerBar");
    return cart_item !== null && (cart_item === true || cart_item === "true") ? false : true;
  });

  const [offerBarTwo, setofferBarTwo] = useState(() => {
    const cart_item = localStorage.getItem("offerBarTwo");
    return cart_item !== null && (cart_item === true || cart_item === "true") ? false : true;
  });

  /**
   * Marks the user as logged in both in state and localStorage.
   * Called after a successful login or account creation.
   */
  const UpdateDtatususeLoggedIn = () => {
    setUserLoggedIn(true);
    localStorage.setItem("isLoggedin", true);
  };

  // ── Popup / modal visibility flags ──────────────────────────────────────

  /** Open the login modal automatically when the URL hash is #login. */
  const [LoginPopup, setLoginPopup] = useState(false);
  const HideLoginPopup = () => setLoginPopup(false);
  const ShowLoginPopup = () => setLoginPopup(true);

  useEffect(() => {
    // Check if hash is #login
    if (window.location.hash === "#login" && !useLoggedIn) {
      // Check if user is not logged in
        setLoginPopup(true);
        window.history.replaceState(null, '', window.location.pathname);

    }
  }, [useLoggedIn]);

  /**
   * Shows the login modal or redirects to the profile page if already logged in.
   * Used by the header account icon.
   */
  const ShowLoginPopup_ = () => {
    if (useLoggedIn) {
      window.location.href = "/admin-profile";
    } else {
      setLoginPopup(true);
    }
  };

  const [lastURL, setlastURL] = useState(undefined);

  /** Open the create-account modal automatically when the URL hash is #register. */
  const [CreateAccountPopup, setCreateAccountPopup] = useState(window.location.hash === "#register" ? true : false);
  const HideCreateAccountPopup = () => setCreateAccountPopup(false);
  const ShowCreateAccountPopup = () => setCreateAccountPopup(true);

  const [CartSidebarPopup, setCartSidebarPopup] = useState(false);
  const HideCartSidebarPopup = () => setCartSidebarPopup(false);
  const ShowCartSidebarPopup = () => setCartSidebarPopup(true);

  const [AboutPagePopup, setAboutPagePopup] = useState(false);
  const HideAboutPagePopup = () => setAboutPagePopup(false);
  const ShowAboutPagePopup = () => setAboutPagePopup(true);

  const [SignUpPopup, setSignUpPopup] = useState(false);
  const HideSignUpPopup = () => setSignUpPopup(false);
  const ShowSignUpPopup = () => setSignUpPopup(true);

  /** Cookie preference popup — shown until user dismisses it once. */
  const [CookiePrefPopup, setCookiePrefPopup] = useState(() => {
    return localStorage.getItem("user_cookie") !== null ? false : true;
  });
  const HideCookiePrefPopup = () => {
    setCookiePrefPopup(false);
    localStorage.setItem("user_cookie", "enabled");
  };
  const ShowCookiePrefPopup = () => setCookiePrefPopup(true);

  // ── Forgot-password popup flags ──────────────────────────────────────────
  const [prodcut_list_order, set_prodcut_list_order] = useState(true);

  /** Opens the cart sidebar. Passed as a callback to add-to-cart buttons. */
  const redirectTocart = () => {
    ShowCartSidebarPopup();
  };

  /**
   * Closes the login modal and opens the create-account modal.
   * Triggered by the "Register" link inside the login popup.
   */
  const OpenRegisterPopupFromLogin = () => {
    setLoginPopup(false);
    setCreateAccountPopup(true);
  };

  // ── Logged-in user details ───────────────────────────────────────────────

  /** Reads persisted user details from localStorage on first render. */
  const [LoggedUserDetails, setUpdateloginUserDetail] = useState(() => {
    const localdetail = localStorage.getItem("userDetail");
    return localdetail !== null ? JSON.parse(localdetail) : undefined;
  });

  const [LoginErrorMsgm, setLoginErrorMessage] = useState(undefined);

  /**
   * Persists user credentials after a successful login, updates state,
   * and redirects away from activation pages back to home.
   *
   * @param {string} useremail
   * @param {string} user_token  - Shopify customer access token
   * @param {string} first_name
   * @param {string} last_name
   * @param {string} mobile
   * @param {string} redirect_url - URL to navigate to after login
   */
  const DoLogin = (useremail, user_token, first_name, last_name, mobile, redirect_url) => {
    const SaveDetail = {
      email: useremail,
      accessToken: user_token,
      firstName: first_name,
      lastName: last_name,
      phone: mobile,
    };
    localStorage.setItem("userDetail", JSON.stringify(SaveDetail));
    setUpdateloginUserDetail(SaveDetail);
    UpdateDtatususeLoggedIn(true);

    window.location.href =
      redirect_url === "/activate" || redirect_url === "/activate-guest"
        ? "/"
        : redirect_url;
  };

  /**
   * On mount, validates the stored access token against the engine API.
   * If the token is invalid or expired, clears all auth data and redirects home.
   */
  useEffect(() => {
    if (
      LoggedUserDetails !== undefined &&
      LoggedUserDetails !== null &&
      LoggedUserDetails.hasOwnProperty("accessToken") &&
      LoggedUserDetails.accessToken !== "" &&
      LoggedUserDetails.accessToken !== null
    ) {
      if (LoggedUserDetails !== undefined && LoggedUserDetails.email !== undefined && LoggedUserDetails.accessToken !== undefined) {
        axios.post(
          EngineURL + "create-account/2fa/validate_login_token.php",
          {
            email: LoggedUserDetails.email,
            login_token: LoggedUserDetails.accessToken,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${LoggedUserDetails.accessToken}`,
            },
          }
        ).then(function (response) {
          if (response.data !== undefined && response.data !== null && response.data.code !== undefined && response.data.code === 200) {
            setUserLoggedIn(true);
            localStorage.setItem("isLoggedin", true);
          } else {
            // Token rejected — wipe all session data and force re-login.
            localStorage.removeItem("userDetail");
            localStorage.setItem("isLoggedin", false);
            localStorage.removeItem("checkout_token");
            localStorage.removeItem("thank_you_image");
            localStorage.removeItem("checkout_id");
            localStorage.removeItem("cart_session");
            localStorage.removeItem("create_account_detail");
            localStorage.removeItem("cart_session_sku");
            localStorage.removeItem("isLoggedin");
            localStorage.removeItem("was_book");
            localStorage.removeItem("wishlist_item");
            window.location.href = "/";
          }
        }).catch(function (error) {
          console.log(error);
        });
      }
    }
  }, [LoggedUserDetails]);

  // ── Cart / checkout state ────────────────────────────────────────────────

  /**
   * Reads the stored Shopify cart ID from localStorage.
   * Clears all session data and redirects home if the cart contains user errors
   * (e.g. the cart has expired on Shopify's side).
   */
  const [CheckoutID, setCheckoutID] = useState(() => {
    const cart_item = localStorage.getItem("checkout_id");
    if (cart_item === null) return undefined;
    const cart_item_json = JSON.parse(cart_item);
    if (cart_item_json.userErrors && cart_item_json.userErrors.length !== 0) {
      localStorage.removeItem("userDetail");
      localStorage.setItem("isLoggedin", false);
      localStorage.removeItem("checkout_token");
      localStorage.removeItem("thank_you_image");
      localStorage.removeItem("checkout_id");
      localStorage.removeItem("cart_session");
      localStorage.removeItem("create_account_detail");
      localStorage.removeItem("cart_session_sku");
      localStorage.removeItem("isLoggedin");
      localStorage.removeItem("was_book");
      window.location.href = "/";
      return undefined;
    }
    return cart_item_json.cart ? cart_item_json.cart.id : undefined;
  });

  /** Tracks whether this is the first render — prevents triggering cart mutations on load. */
  const [InitialLoad, setInitialLoad] = useState(true);

  const [CardItem, AdditemTOcart] = useState(() => {
    const cart_item = localStorage.getItem("cart_session");
    return cart_item !== null ? JSON.parse(cart_item) : { lines: [] };
  });

  const [CardItem_sku, AdditemTOcart_sku] = useState(() => {
    const cart_item = localStorage.getItem("cart_session_sku");
    return cart_item !== null ? JSON.parse(cart_item) : { lines: [] };
  });

  const [CheckoutData, setCheckout] = useState(() => {
    const cart_item = localStorage.getItem("checkout_id");
    return cart_item !== null ? JSON.parse(cart_item) : undefined;
  });

  // ── localStorage cart helpers ─────────────────────────────────────────────
  // These read fresh values from localStorage instead of relying on potentially
  // stale closure state — important inside async callbacks and effects.

  /** Returns the current Shopify cart ID from localStorage. */
  const getCheckoutID = () => {
    const cart_item = localStorage.getItem("checkout_id");
    if (cart_item === null) return undefined;
    const cart_item_json = JSON.parse(cart_item);
    return cart_item_json.cart ? cart_item_json.cart.id : undefined;
  };

  /** Returns the current SKU-enriched cart lines from localStorage. */
  const getCardItem_sku = () => {
    const cart_item = localStorage.getItem("cart_session_sku");
    return cart_item !== null ? JSON.parse(cart_item) : { lines: [] };
  };

  /** Returns the full checkout/cart object from localStorage. */
  const getCheckoutData = () => {
    const cart_item = localStorage.getItem("checkout_id");
    return cart_item !== null ? JSON.parse(cart_item) : undefined;
  };

  /**
   * Empties the cart after a completed purchase — resets the in-memory cart
   * state (so the header badge + cart sidebar update immediately, no reload)
   * AND clears the cart's localStorage. `checkout_token` is intentionally kept:
   * the thank-you page needs it to fetch the order details.
   */
  const resetCart = () => {
    // Clear persisted cart FIRST so any effect that re-reads localStorage sees it empty.
    localStorage.removeItem("checkout_id");
    localStorage.removeItem("cart_session");
    localStorage.removeItem("cart_session_sku");
    // Reset all in-memory cart state: badge + sidebar (CheckoutData/ID) AND the
    // line-item stores (CardItem/CardItem_sku) which can otherwise rebuild the cart.
    setCheckout(undefined);
    setCheckoutID(undefined);
    AdditemTOcart({ lines: [] });
    AdditemTOcart_sku({ lines: [] });
    setCartqty(0);
    setCartAmount(0.0);
  };

  /**
   * Safety net for emptying the cart after purchase. The cart is also cleared on
   * the /thank-you page, but if Shopify returns the user to a different page,
   * this confirms with the engine whether the order was actually placed for the
   * saved checkout token and, only then, empties the cart. An abandoned checkout
   * (no order) keeps its cart. Runs once per full load while a checkout is pending.
   */
  useEffect(() => {
    const token = localStorage.getItem("checkout_token");
    const hasCart = localStorage.getItem("checkout_id");
    if (!token || !hasCart) return;
    axios
      .post(EngineURL + "chk/get-db-order-data.php", { checkout_token: token })
      .then((res) => {
        const data = res && res.data;
        if (!data || data.status !== "success" || !data.data) return;
        // Only a genuinely PLACED order carries a Shopify order payload. A
        // started-but-incomplete / abandoned checkout returns "success" with no
        // shopify_response — it must NOT wipe the cart (otherwise the cart shows
        // 0 on every reload after visiting checkout). Mirrors Thank-you's check.
        let orderPlaced = false;
        try {
          orderPlaced =
            data.data.shopify_response != null &&
            JSON.parse(data.data.shopify_response) !== null;
        } catch (e) {
          orderPlaced = false;
        }
        if (orderPlaced) {
          resetCart();
          localStorage.removeItem("checkout_token");
        }
      })
      .catch(() => { });
  }, []);

  /** Syncs cart quantity and amount display whenever CheckoutData changes. */
  useEffect(() => {
    if (CheckoutData !== undefined) {
      UpdateCartAmount(CheckoutData.cart.cost.totalAmount);
      let prc = 0;
      CheckoutData.cart.lines.edges.forEach((item) => {
        prc += item.node.quantity;
      });
      UpdateCartQty(prc);
    }
  }, [CheckoutData]);

  // ── GraphQL cart mutations ───────────────────────────────────────────────

  /** Creates a new Shopify cart. Used when no existing cart ID is stored. */
  const [CreateCheckoutQL, { loading: Addtocartloading, data: Addtocartdata }] = useMutation(createCheckout, {
    variables: { CardItem },
  });

  /** Adds lines to an existing Shopify cart. */
  const [AddmoreItemInCar, { loading: AddtocartMoreloading, data: AddtocartMoredata }] = useMutation(checkoutLineItemsAdd);

  /** Updates the quantity of an existing cart line. */
  const [UpdateCartItemQuery, { loading: UpdateItemLoading, data: UpdatedData }] = useMutation(checkoutLineItemsUpdate);

  /** Removes a line from the cart. */
  const [RemoveCartItemQuery, { loading: RemoveItemLoading, data: RemovedData }] = useMutation(checkoutLineItemsRemove);

  /**
   * Adds a product variant to the cart (or creates the cart if it doesn't exist).
   * Also opens the cart sidebar and updates both the simple and SKU-enriched cart sessions.
   *
   * @param {string} merchandiseId     - Shopify variant GID
   * @param {number} Quantity
   * @param {string} sku
   * @param {string} stream_type
   * @param {string} record_type
   * @param {Array}  tags
   * @param {string} pimage            - Product image URL
   * @param {string} slug              - Product handle/slug
   * @param {string} start_date
   * @param {string} program_structure - Program type slug
   */
  const AddtoCart = (merchandiseId, Quantity, sku, stream_type, record_type, tags, pimage, slug, start_date, program_structure) => {
    setInitialLoad(false);

    const newItem = { merchandiseId, quantity: Quantity };

    const newItemSku = {
      merchandiseId,
      quantity: Quantity,
      sku,
      stream_type,
      record_type,
      tags,
      pimage,
      slug,
      start_date,
      program_structure,
    };

    let CartItemSku = getCardItem_sku();
    let updatedCartSku = [];

    if (CartItemSku.lines.length < 1) {
      updatedCartSku = [newItemSku];
    } else {
      let itemExists = false;

      // If the variant already exists in the cart, increment its quantity.
      updatedCartSku = CartItemSku.lines.map((cartdata) =>
        cartdata.merchandiseId === merchandiseId
          ? ((itemExists = true), { ...cartdata, quantity: cartdata.quantity + Quantity })
          : cartdata
      );

      if (!itemExists) updatedCartSku.push(newItemSku);
    }

    const mylineitem = { lines: [newItem] };
    const mylineitem_sku = { lines: updatedCartSku };

    redirectTocart();

    AdditemTOcart(mylineitem);
    localStorage.setItem("cart_session", JSON.stringify(mylineitem));

    AdditemTOcart_sku(mylineitem_sku);
    localStorage.setItem("cart_session_sku", JSON.stringify(mylineitem_sku));
  };

  /**
   * Fires whenever CardItem changes (i.e. after AddtoCart is called).
   * Decides whether to create a new Shopify cart or add lines to an existing one.
   */
  useEffect(() => {
    if (!InitialLoad) {
      let CheckoutData = getCheckoutData();

      if (CheckoutData !== undefined) {
        if (CheckoutData.cart.lines.edges.length < 1) {
          CreateCheckoutQL();
        } else {
          AddmoreItemInCar({
            variables: {
              checkoutId: CheckoutData.cart.id,
              lines: CardItem.lines,
            },
          });
        }
      } else if (CardItem.lines.length) {
        CreateCheckoutQL();
      }
      setInitialLoad(false);
    }
  }, [CardItem]);

  /** Persists the newly created cart to localStorage and state after createCheckout resolves. */
  useEffect(() => {
    if (Addtocartdata !== undefined) {
      setCheckout(Addtocartdata.cartCreate);
      localStorage.setItem("checkout_id", JSON.stringify(Addtocartdata.cartCreate));
      setCheckoutID(Addtocartdata.cartCreate.cart.id);
      // A brand-new cart supersedes any earlier checkout attempt, so drop the old
      // token — prevents the completion check from clearing this fresh cart.
      localStorage.removeItem("checkout_token");
    }
  }, [Addtocartdata]);

  /** Persists the updated cart to localStorage after cartLinesAdd resolves. */
  useEffect(() => {
    if (AddtocartMoredata !== undefined) {
      setCheckout(AddtocartMoredata.cartLinesAdd);
      localStorage.setItem("checkout_id", JSON.stringify(AddtocartMoredata.cartLinesAdd));
    }
  }, [AddtocartMoredata]);

  /**
   * Removes a single line item from the Shopify cart.
   *
   * @param {number} qty    - Unused; kept for API consistency with UpdateCartItem.
   * @param {string} lineID - Shopify cart line GID to remove.
   */
  function removeItemfromCart(qty, lineID) {
    let CheckoutID = getCheckoutID();
    const variables = { checkoutId: CheckoutID, lineItemIds: [lineID] };
    RemoveCartItemQuery({ variables });
  }

  /** Persists the cart and reloads the attendees page after a line is removed. */
  useEffect(() => {
    if (RemovedData !== undefined) {
      setCheckout(RemovedData.cartLinesRemove);
      localStorage.setItem("checkout_id", JSON.stringify(RemovedData.cartLinesRemove));

      if (window.location.pathname === "/attendees") {
        window.location.reload();
      }
    }
  }, [RemovedData]);

  /**
   * Updates the quantity of an existing cart line item.
   *
   * @param {number} qty    - New quantity value.
   * @param {string} lineID - Shopify cart line GID to update.
   */
  /**
   * Updates a cart line quantity. Single source of truth for both the cart
   * sidebar and the delivery page.
   *
   * Optimistic + flash-free: the shared CheckoutData state (and localStorage)
   * is updated immediately with the new quantity and recomputed amounts, so the
   * UI reflects the change instantly with no spinner or page reload. The Shopify
   * mutation then runs silently and its response reconciles the exact amounts.
   *
   * @param {number|string} qty    - New quantity (1+).
   * @param {string}        lineID - Shopify cart line id.
   */
  function UpdateCartItem(qty, lineID) {
    const newQty = parseInt(qty, 10);
    if (!newQty || newQty < 1) return;

    // ── Optimistic update of the shared cart state + localStorage ──
    setCheckout((prev) => {
      if (prev === undefined) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      let subtotal = 0;
      next.cart.lines.edges.forEach((edge) => {
        if (edge.node.id === lineID) edge.node.quantity = newQty;
        const unit = parseFloat(edge.node.merchandise.price.amount) || 0;
        const lineTotal = unit * edge.node.quantity;
        subtotal += lineTotal;
        if (edge.node.cost && edge.node.cost.totalAmount) {
          edge.node.cost.totalAmount.amount = lineTotal.toFixed(2);
        }
      });
      if (next.cart.cost) {
        if (next.cart.cost.subtotalAmount) next.cart.cost.subtotalAmount.amount = subtotal.toFixed(2);
        if (next.cart.cost.totalAmount) next.cart.cost.totalAmount.amount = subtotal.toFixed(2);
      }
      localStorage.setItem("checkout_id", JSON.stringify(next));
      return next;
    });

    // ── Silently sync with Shopify; response reconciles exact amounts ──
    UpdateCartItemQuery({
      variables: {
        checkoutId: getCheckoutID(),
        lines: [{ id: lineID, quantity: newQty }],
      },
    });
  }

  /** Reconciles the cart with Shopify's authoritative response (no reload). */
  useEffect(() => {
    if (UpdatedData !== undefined) {
      setCheckout(UpdatedData.cartLinesUpdate);
      localStorage.setItem("checkout_id", JSON.stringify(UpdatedData.cartLinesUpdate));
    }
  }, [UpdatedData]);

  const customerAccessToken = LoggedUserDetails !== undefined ? LoggedUserDetails.accessToken : undefined;

  /** Placeholder — intended to associate the logged-in customer with the checkout. */
  function Logged_In_user_associative_with_checkout() { }

  // ── Store page / navigation data ─────────────────────────────────────────

  const [pageJson, setpageJson] = useState([]);
  const [ProductPageJson, setProductPageJson] = useState([]);

  /** Fetches the store page menu data from the engine on first render. */
  useEffect(() => {
    if (ProductPageJson.length === 0) {
      const cacheBust = (Math.random() + 1).toString(36).substring(7);
      axios.get(EngineURL + "sync/update-page-item/store_page_data.php?var=" + cacheBust).then(function (response) {
        setProductPageJson(response.data);
      }).catch(function (error) {

        console.error("store-page-data fetch failed:", error.message);
      });
    }
  }, []);

  // useEffect(() => {
  //   if (ProductPageJson.length === 0) {
  //     const cacheBust = (Math.random() + 1).toString(36).substring(7);
  //     axios.get(EngineURL + "sync/update-page-item/store-page-data.json?var=" + cacheBust).then(function (response) {
  //       setProductPageJson(response.data);
  //     });
  //   }
  // }, []);

  /** Sorts the page menu by Menu_Order__c whenever the raw data changes. */
  useEffect(() => {
    setpageJson(
      ProductPageJson.sort(function (a, b) {
        return a.Menu_Order__c - b.Menu_Order__c;
      })
    );
  }, [ProductPageJson]);

  // ── Scroll-reveal for headings ────────────────────────────────────────────
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('lc-reveal');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    const observeNew = () => {
      document.querySelectorAll('h1, h2, h3, h4, .advantage_block').forEach((el) => {
        if (!el.closest('.hero_banner_content') && !el.hasAttribute('data-lc-obs')) {
          el.setAttribute('data-lc-obs', '1');
          el.classList.add('lc-reveal-pending');
          io.observe(el);
        }
      });
    };

    observeNew();

    const mo = new MutationObserver(observeNew);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => { io.disconnect(); mo.disconnect(); };
  }, []);

  // ── Header search ─────────────────────────────────────────────────────────
  const [HeaderSearchText, setHeaderSearchText] = useState("");
  

  // ── Can We Help widget ────────────────────────────────────────────────────
  const [CanWeHelpStatus, setCanWeHelpStatus] = useState(false);

  // ── Popular search tags ───────────────────────────────────────────────────
  const [PopularTag, setPopularTag] = useState([]);

  /**
   * Fetches the home-page popular filter tags from the admin panel API.
   */
  const fetchTag = async () => {
    try {
      const home_page_filters = AdminPanelURL + "api/home_page_filters";

      const home_page_filters_response = await axios({
          method: 'get',
          url: home_page_filters,
          headers: {
              'X-API-KEY': API_KEY   // ← Add this
          }
      });

      if (home_page_filters_response.status === 200) {
        var data = home_page_filters_response.data;
        if (data.length !== 0) {
          setPopularTag(data.filters);
        }
      }
    } catch (error) {
      // Silently ignore — popular tags are non-critical
    }
  };

  useEffect(() => {
    fetchTag();
  }, []);

  // ── Wishlist ──────────────────────────────────────────────────────────────

  /**
   * Toggles a product in/out of the user's wishlist.
   * If logged in, syncs the change to the engine API immediately.
   *
   * @param {string}      productid - Product slug / handle.
   * @param {string|null} pdates    - Program start date string or null.
   */
  const AddtoWishlist = (productid, pdates) => {
    if (!useLoggedIn) {
      ShowLoginPopup();
      return;
    }

    let wishlistItem =
      localStorage.getItem("wishlist_item") !== undefined &&
        localStorage.getItem("wishlist_item") !== "undefined" &&
        localStorage.getItem("wishlist_item") !== null
        ? JSON.parse(localStorage.getItem("wishlist_item"))
        : [];
    let wishlistItemArray =
      localStorage.getItem("wishlist_item_array") !== null
        ? JSON.parse(localStorage.getItem("wishlist_item_array"))
        : [];

    let NewwishlistItem = [];
    let NewwishlistItemArray = [];

    if (!wishlistItem.includes(productid)) {
      NewwishlistItem.push(productid);
      let formattedDate = "";
      if (pdates !== null) {
        var dt = new Date(pdates);
        formattedDate =
          dt.getFullYear() +
          "-" +
          (dt.getMonth() + 1).toString().padStart(2, "0") +
          "-" +
          dt.getDate().toString().padStart(2, "0");
      }
      NewwishlistItemArray.push({ handle: productid, date: formattedDate });

      let userDetail = JSON.parse(localStorage.getItem("userDetail"));
      axios
        .post(EngineURL + "wishlist/wishlist_add.php", {
          contact_id: userDetail.email,
          p_id: productid,
          date: formattedDate,
        },{
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${LoggedUserDetails.accessToken}`,
            },
          })
        .then(function (response) { })
        .catch(function (error) { });
    } else {
      RemoveItemWhishlst(productid);
    }

    wishlistItem.forEach((data) => {
      if (data !== productid) {
        NewwishlistItem.push(data);
      }
    });

    wishlistItemArray.forEach((data) => {
      if (data.handle !== productid) {
        NewwishlistItemArray.push(data);
      }
    });

    setgetAllWishlist(NewwishlistItem);
    localStorage.setItem("wishlist_item", JSON.stringify(NewwishlistItem));
    localStorage.setItem("wishlist_item_array", JSON.stringify(NewwishlistItemArray));
  };

  /**
   * Calls the engine API to remove a product from the logged-in user's wishlist.
   *
   * @param {string} productid - Product slug / handle.
   */
  const RemoveItemWhishlst = (productid) => {
    if (useLoggedIn) {
      let userDetail = JSON.parse(localStorage.getItem("userDetail"));
      let Wishlistdata = {
        contact_id: userDetail.email,
        p_id: productid,
      };

      axios
        .post(EngineURL + "wishlist/wishlist_delete.php", Wishlistdata,{
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${LoggedUserDetails.accessToken}`,
            },
          })
        .then(function (response) { })
        .catch(function (error) { });
    }
  };

  const [getAllWishlist, setgetAllWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);

  /**
   * Fetches the server-side wishlist for the currently logged-in user
   * and syncs it to localStorage.
   */
  const FetchWishlistData = async () => {
    let userDetail = JSON.parse(localStorage.getItem("userDetail"));    

      axios
      .post(EngineURL + "wishlist/wishlist_get.php",
          { contact_id: userDetail.email },
          {
              headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${LoggedUserDetails.accessToken}`,
              },
          }
      )
      .then(function (response) {
          if (response.status === 200) {
              const result = response.data.result || [];
              setgetAllWishlist(result);
              if (result.length > 0) {
                  localStorage.setItem("wishlist_item", JSON.stringify(result));
              }
          }
          setWishlistLoading(false);
      })
      .catch(function (error) { setWishlistLoading(false); });
  };

  /**
   * On mount: if logged in and local items exist, sync them to the API then fetch the
   * authoritative list. If logged in with no local items, just fetch. If guest, load
   * from localStorage.
   */
  useEffect(() => {
    const localItems = localStorage.getItem("wishlist_item");
    const wishlistItem =
      localItems !== undefined && localItems !== "undefined" && localItems !== null
        ? JSON.parse(localItems)
        : [];
    const localArr = localStorage.getItem("wishlist_item_array");
    const wishlistItemArray = localArr ? JSON.parse(localArr) : [];
    if (useLoggedIn) {
      if (wishlistItem.length !== 0) {
        let userDetail = JSON.parse(localStorage.getItem("userDetail"));
        axios
          .post(EngineURL + "wishlist/wishlist_add.php", {
            contact_id: userDetail.email,
            p_id: wishlistItem.join(","),
            date: wishlistItemArray.map((i) => i.date).join(","),
          },{
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${LoggedUserDetails.accessToken}`,
            },
          })
          .then(function () {
            FetchWishlistData();
          })
          .catch(function () { });
      } else {
        FetchWishlistData();
      }
    } else if (wishlistItem.length !== 0) {
      setgetAllWishlist(wishlistItem);
      setWishlistLoading(false);
    } else {
      setWishlistLoading(false);
    }
  }, []);

  // ── Greeting ──────────────────────────────────────────────────────────────

  /**
   * Returns a time-of-day greeting string.
   * @returns {"Good morning" | "Good afternoon" | "Good evening"}
   */
  const getGreeting = () => {
    const curHr = new Date().getHours();
    if (curHr < 12) return "Good morning";
    if (curHr < 18) return "Good afternoon";
    return "Good evening";
  };
  const Good = getGreeting();

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Router>
        <Header
          setHeaderSearchText={setHeaderSearchText}
          offerBar={offerBar}
          setofferBar={setofferBar}
          ProductPageJson={pageJson}
          redirectTocart={redirectTocart}
          ShowAboutPagePopup={ShowAboutPagePopup}
          ShowLoginPopup={ShowLoginPopup}
          useLoggedIn={useLoggedIn}
          ShowLoginPopup_={ShowLoginPopup_}
          CartAmount={CartAmount}
          ShowCreateAccountPopup={ShowCreateAccountPopup}
          AdminPanelURL={AdminPanelURL}
          ShowSignUpPopup={ShowSignUpPopup}
          Cartqty={Cartqty}
          API_KEY={API_KEY}
        />
        <ScrollToTop />

        <div className="main position-relative">
          <Switch>
            <Route exact path="/">
              <HomePage
                offerBarTwo={offerBarTwo}
                setofferBarTwo={setofferBarTwo}
                lastURL={lastURL}
                setlastURL={setlastURL}
                ShowLoginPopup={ShowLoginPopup}
                useLoggedIn={useLoggedIn}
                Program_structure_images={Program_structure_images}
                EngineURL={EngineURL}
                API_KEY={API_KEY}
                prodcut_list_order={prodcut_list_order}
                set_prodcut_list_order={set_prodcut_list_order}
                ProductPageJson={ProductPageJson}
                AddtoCart={AddtoCart}
                UpdateCartQty={UpdateCartQty}
                ShowSignUpPopup={ShowSignUpPopup}
                UserData={LoggedUserDetails}
                AdminPanelURL={AdminPanelURL}
                PopularTag={PopularTag}
                UpdateCartAmount={UpdateCartAmount}
                AddtoWishlist={AddtoWishlist}
                getAllWishlist={getAllWishlist}
              />
            </Route>
            <Route path="/product/:slug">
              <SIngleProduct
                AddtoWishlist={AddtoWishlist}
                getAllWishlist={getAllWishlist}
                EngineURL={EngineURL}
                Program_structure_images={Program_structure_images}
                Program_structure_color={Program_structure_color}
                AddtoCart={AddtoCart}
                UpdateCartQty={UpdateCartQty}
                UpdateCartAmount={UpdateCartAmount}
                CheckoutData={CheckoutData}
                UpdateCartItem={UpdateCartItem}
              />
            </Route>

            {/* Dynamic routes — one per active store page menu item */}
            {ProductPageJson.map((menu, index) => {
              const menuname = menu.Name;
              if (menuname === null || menuname === undefined || menu.Status !== "Active"){
                return null;
              }
              const slug = menuname.split(" ").join("-").toLowerCase();
              return (
                <Route path={"/" + slug} key={menuname}>
                  <ProductPage
                    key={menuname + "111"}
                    AddtoWishlist={AddtoWishlist}
                    getAllWishlist={getAllWishlist}
                    PopularTag={PopularTag}
                    ShowSignUpPopup={ShowSignUpPopup}
                    offerBarTwo={offerBarTwo}
                    setofferBarTwo={setofferBarTwo}
                    lastURL={lastURL}
                    setlastURL={setlastURL}
                    EngineURL={EngineURL}
                    Program_structure_images={Program_structure_images}
                    prodcut_list_order={prodcut_list_order}
                    set_prodcut_list_order={set_prodcut_list_order}
                    ProductPageJson={ProductPageJson}
                    AddtoCart={AddtoCart}
                    UpdateCartQty={UpdateCartQty}
                    ShowLoginPopup={ShowLoginPopup}
                    useLoggedIn={useLoggedIn}
                    UserData={LoggedUserDetails}
                    AdminPanelURL={AdminPanelURL}
                    Program_structure_color={Program_structure_color}
                    storeURL={storeURL}
                    UpdateCartAmount={UpdateCartAmount}
                    API_KEY={API_KEY}
                  />
                </Route>
              );
            })}

            <Route path="/search">
              <Search
                AddtoWishlist={AddtoWishlist}
                getAllWishlist={getAllWishlist}
                Program_structure_color={Program_structure_color}
                PopularTag={PopularTag}
                ShowSignUpPopup={ShowSignUpPopup}
                offerBarTwo={offerBarTwo}
                setofferBarTwo={setofferBarTwo}
                lastURL={lastURL}
                setlastURL={setlastURL}
                EngineURL={EngineURL}
                Program_structure_images={Program_structure_images}
                prodcut_list_order={prodcut_list_order}
                set_prodcut_list_order={set_prodcut_list_order}
                ProductPageJson={ProductPageJson}
                AddtoCart={AddtoCart}
                UpdateCartQty={UpdateCartQty}
                ShowLoginPopup={ShowLoginPopup}
                useLoggedIn={useLoggedIn}
                UserData={LoggedUserDetails}
                AdminPanelURL={AdminPanelURL}
                storeURL={storeURL}
                UpdateCartAmount={UpdateCartAmount}
                API_KEY={API_KEY}
              />
            </Route>

            <Route path="/all-courses">
              <Search
                AddtoWishlist={AddtoWishlist}
                getAllWishlist={getAllWishlist}
                Program_structure_color={Program_structure_color}
                PopularTag={PopularTag}
                ShowSignUpPopup={ShowSignUpPopup}
                offerBarTwo={offerBarTwo}
                setofferBarTwo={setofferBarTwo}
                lastURL={lastURL}
                setlastURL={setlastURL}
                EngineURL={EngineURL}
                Program_structure_images={Program_structure_images}
                prodcut_list_order={prodcut_list_order}
                set_prodcut_list_order={set_prodcut_list_order}
                ProductPageJson={ProductPageJson}
                AddtoCart={AddtoCart}
                UpdateCartQty={UpdateCartQty}
                ShowLoginPopup={ShowLoginPopup}
                useLoggedIn={useLoggedIn}
                UserData={LoggedUserDetails}
                AdminPanelURL={AdminPanelURL}
                storeURL={storeURL}
                UpdateCartAmount={UpdateCartAmount}
                API_KEY={API_KEY}
              />
            </Route>

            <Route path="/my-wishlist">
              <AdminWishlist
                Good={Good}
                UpdateloginUserDetail={LoggedUserDetails}
                AddtoWishlist={AddtoWishlist}
                getAllWishlist={getAllWishlist}
                wishlistLoading={wishlistLoading}
                EngineURL={EngineURL}
                useLoggedIn={useLoggedIn}
                Program_structure_images={Program_structure_images}
                Program_structure_color={Program_structure_color}
                loggedInUserData={LoggedUserDetails}
                UpdateCartQty={UpdateCartQty}
                AddtoCart={AddtoCart}
                UpdateCartAmount={UpdateCartAmount}
                ShowLoginPopup={ShowLoginPopup}
                ShowLoginPopup_={ShowLoginPopup_}
              />
            </Route>
            <Route path="/admin-my-events">
              <AdminMyEvents
                Good={Good}
                UpdateloginUserDetail={LoggedUserDetails}
                EngineURL={EngineURL}
                useLoggedIn={useLoggedIn}
                loggedInUserData={LoggedUserDetails}
                UpdateCartQty={UpdateCartQty}
                UpdateCartAmount={UpdateCartAmount}
                ShowLoginPopup={ShowLoginPopup}
                ShowLoginPopup_={ShowLoginPopup_}
              />
            </Route>
            <Route path="/admin-orders">
              <AdminOrders
                Good={Good}
                UpdateloginUserDetail={LoggedUserDetails}
                EngineURL={EngineURL}
                useLoggedIn={useLoggedIn}
                loggedInUserData={LoggedUserDetails}
                UpdateCartQty={UpdateCartQty}
                UpdateCartAmount={UpdateCartAmount}
                ShowLoginPopup={ShowLoginPopup}
                ShowLoginPopup_={ShowLoginPopup_}
              />
            </Route>

            <Route path="/admin-cpd-points">
              <AdminCPDPoints
                Good={Good}
                UpdateloginUserDetail={LoggedUserDetails}
                EngineURL={EngineURL}
                useLoggedIn={useLoggedIn}
                loggedInUserData={LoggedUserDetails}
                UpdateCartQty={UpdateCartQty}
                UpdateCartAmount={UpdateCartAmount}
                ShowLoginPopup={ShowLoginPopup}
                ShowLoginPopup_={ShowLoginPopup_}
                AdminPanelURL={AdminPanelURL}
                API_KEY={API_KEY}
              />
            </Route>
            <Route path="/admin-my-library">
              <AdminMyLibrary
                Good={Good}
                UpdateloginUserDetail={LoggedUserDetails}
                EngineURL={EngineURL}
                Program_structure_images={Program_structure_images}
                useLoggedIn={useLoggedIn}
                loggedInUserData={LoggedUserDetails}
                UpdateCartQty={UpdateCartQty}
                UpdateCartAmount={UpdateCartAmount}
                ShowLoginPopup={ShowLoginPopup}
                ShowLoginPopup_={ShowLoginPopup_}
              />
            </Route>

            <Route path="/reset">
              <ResetPassword ShowLoginPopup={ShowLoginPopup} />
            </Route>

            <Route path="/activate-guest">
              <ActivateAccountGuest EngineURL={EngineURL} ShowLoginPopup={ShowLoginPopup} />
            </Route>
            <Route path="/admin-profile">
              <AdminProfile
                Good={Good}
                UpdateloginUserDetail={LoggedUserDetails}
                useLoggedIn={useLoggedIn}
                ShowLoginPopup_={ShowLoginPopup}
                UpdateCartQty={UpdateCartQty}
                EngineURL={EngineURL}
                UpdateCartAmount={UpdateCartAmount}
              />
            </Route>

            <Route path="/attendees">
              <Attendees
                EngineURL={EngineURL}
                Program_structure_images={Program_structure_images}
                ShowLoginPopup={ShowLoginPopup}
                CardItem_sku={CardItem_sku}
                UserData={LoggedUserDetails}
                useLoggedIn={useLoggedIn}
                CartData={CheckoutData}
                customerAccessToken={customerAccessToken}
                ShowCreateAccountPopup={ShowCreateAccountPopup}
                HideCreateAccountPopup={HideCreateAccountPopup}
                setAddresstoCheckout={Logged_In_user_associative_with_checkout}
                UpdateCartQty={UpdateCartQty}
                UpdateCartAmount={UpdateCartAmount}
                UpdateCartItem={UpdateCartItem}
              />
            </Route>

            <Route path="/thank-you">
              <Thankyou
                EngineURL={EngineURL}
                ShowCreateAccountPopup={ShowCreateAccountPopup}
                ShowLoginPopup={ShowLoginPopup}
                Program_structure_images={Program_structure_images}
                useLoggedIn={useLoggedIn}
                UpdateCartQty={UpdateCartQty}
                UpdateCartAmount={UpdateCartAmount}
                resetCart={resetCart}
              />
            </Route>
            <Route path="*" component={NotFound} status={404} />
          </Switch>
        </div>

        <CookieConsent
          location="bottom"
          buttonText="Accept All"
          declineButtonText="Decline"
          enableDeclineButton
          flipButtons
          cookieName="myAwesomeCookieName2"
          disableStyles
          containerClasses="lc-cookie-bar"
          contentClasses="lc-cookie-bar__content"
          buttonWrapperClasses="lc-cookie-bar__actions"
          buttonClasses="lc-cookie-bar__btn lc-cookie-bar__btn--accept"
          declineButtonClasses="lc-cookie-bar__btn lc-cookie-bar__btn--decline"
          expires={150}
        >
          <p className="lc-cookie-bar__title">Cookie Policy</p>
          <p className="lc-cookie-bar__body">This website uses cookies to enhance the user experience.</p>
        </CookieConsent>

        <Footer EngineURL={EngineURL} AdminPanelURL={AdminPanelURL} setCanWeHelpStatus={setCanWeHelpStatus} />

        <Login
          OpenRegisterPopupFromLogin={OpenRegisterPopupFromLogin}
          LoginPopup={LoginPopup}
          HideLoginPopup={HideLoginPopup}
          EngineURL={EngineURL}
          DoLogin={DoLogin}
          setLoginErrorMessage={setLoginErrorMessage}
          LoginErrorMsgm={LoginErrorMsgm}
          useLoggedIn={useLoggedIn}
        />

        <CreateAccount
          EngineURL={EngineURL}
          ShowLoginPopup={ShowLoginPopup}
          DoLogin={DoLogin}
          UpdateDtatususeLoggedIn={UpdateDtatususeLoggedIn}
          CreateAccountPopup={CreateAccountPopup}
          ShowCreateAccountPopup={ShowCreateAccountPopup}
          HideCreateAccountPopup={HideCreateAccountPopup}
        />

        <CartSidebar
          UpdateCartItem={UpdateCartItem}
          removeItemfromCart={removeItemfromCart}
          CartData={CheckoutData}
          CardItem_sku={CardItem_sku}
          Addtocartloading={Addtocartloading}
          AddtocartMoreloading={AddtocartMoreloading}
          RemoveItemLoading={RemoveItemLoading}
          UpdateItemLoading={UpdateItemLoading}
          CartSidebarPopup={CartSidebarPopup}
          HideCartSidebarPopup={HideCartSidebarPopup}
        />

        <AboutPage AboutPagePopup={AboutPagePopup} ShowAboutPagePopup={ShowAboutPagePopup} HideAboutPagePopup={HideAboutPagePopup} />

        <SignUp EngineURL={EngineURL} SignUpPopup={SignUpPopup} ShowSignUpPopup={ShowSignUpPopup} HideSignUpPopup={HideSignUpPopup} />

        <CanWeHelp EngineURL={EngineURL} CanWeHelpStatus={CanWeHelpStatus} setCanWeHelpStatus={setCanWeHelpStatus} />
        <CookiePref CookiePrefPopup={CookiePrefPopup} HideCookiePrefPopup={HideCookiePrefPopup} ShowCookiePrefPopup={ShowCookiePrefPopup} />
      </Router>
    </>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────
export default App;